/**
 * Input — and `fieldShell()`, the frame recipe every bordered control shares.
 *
 * Every text field, select trigger and date trigger in the product is built from
 * `fieldShell()`, which is why they line up on a shared row without anyone
 * measuring anything.
 *
 * Focus is the product's ONE focus ring: `2px solid var(--focus)` at
 * `outline-offset: 1px`, the same ring `base.css` gives every button, link and
 * icon control, plus a recoloured border. It used to be a 1px *inset*
 * box-shadow, which put two focus vocabularies 90px apart in the same toolbar —
 * the search field wore the weakest ring on the row while Filter, Sort, Columns,
 * density, Export and the primary action beside it all wore the 2px one. An
 * outline paints outside the box and does not move it, so the reason the inset
 * ring existed (never disturb a control's metrics) survives the swap.
 */

import { forwardRef, useCallback, useRef, useState } from 'react';
import type { InputHTMLAttributes, KeyboardEvent, ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { composeRefs } from './Button';
import type { ControlSize } from './Button';

/** How the frame decides it is focused: `within` for real inputs, `visible` for button triggers. */
export type FieldFocusMode = 'within' | 'visible' | 'none';

const FIELD_BASE = [
  'relative flex w-full items-center rounded-sm border bg-surface-1 text-primary',
  'transition-[background-color,border-color,box-shadow,color]',
  'duration-[var(--dur-fast)] ease-[var(--ease-out)]',
].join(' ');

export const FIELD_HEIGHT: Record<ControlSize, string> = {
  sm: 'h-[var(--h-control-sm)]',
  md: 'h-[var(--h-control)]',
  lg: 'h-[var(--h-control-lg)]',
};

export const FIELD_TEXT: Record<ControlSize, string> = {
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-md',
};

/** Horizontal padding that keeps the first glyph on the same optical inset at every size. */
export const FIELD_PAD: Record<ControlSize, string> = {
  sm: 'px-1.5 gap-1.5',
  md: 'px-2 gap-1.5',
  lg: 'px-2.5 gap-2',
};

export interface FieldShellOptions {
  size?: ControlSize;
  invalid?: boolean;
  disabled?: boolean;
  focus?: FieldFocusMode;
  /** Skip the fixed height — for textareas and other auto-height frames. */
  multiline?: boolean;
}

/** The shared bordered-control frame. Callers add their own padding and content. */
export function fieldShell({
  size = 'md',
  invalid = false,
  disabled = false,
  focus = 'within',
  multiline = false,
}: FieldShellOptions = {}): string {
  return cn(
    FIELD_BASE,
    FIELD_TEXT[size],
    !multiline && FIELD_HEIGHT[size],
    'border-line',
    !disabled && 'hover:border-line-strong',

    // The colour rides on the frame unconditionally and the width/offset arrive
    // with the state, exactly as the grid's header buttons and the Company link
    // already do it — an outline whose colour is scoped to `:focus-visible`
    // paints grey for the first frame of the transition.
    'outline-[color:var(--focus)]',
    focus === 'within' &&
      'focus-within:border-focus focus-within:outline-2 focus-within:outline-offset-1',
    focus === 'visible' &&
      'focus-visible:border-focus focus-visible:outline-2 focus-visible:outline-offset-1',

    // Same split as the valid frame above, for the same reason: the ring's
    // COLOUR is unconditional and only its width and offset arrive with the
    // state. `outline-color` is in Tailwind's `transition-colors` set, so a
    // danger ring declared under `focus-visible:` interpolates from
    // `currentColor` — an invalid field's ring painted the field's own text
    // colour for the first `--dur-fast` of every focus. `cn()` is
    // clsx + tailwind-merge, so this later `outline-[color:…]` replaces the
    // `--focus` one rather than racing it. Gated by `scripts/focus-ring-check.mjs`.
    invalid && [
      'border-danger hover:border-danger outline-[color:var(--danger)]',
      focus === 'within' && 'focus-within:border-danger',
      focus === 'visible' && 'focus-visible:border-danger',
    ],

    disabled && 'cursor-not-allowed border-line-subtle bg-surface-3 text-muted shadow-none',
  );
}

/** Fire React's `onChange` on a controlled input we are clearing programmatically. */
function setNativeValue(node: HTMLInputElement, value: string): void {
  const descriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
  descriptor?.set?.call(node, value);
  node.dispatchEvent(new Event('input', { bubbles: true }));
}

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: ControlSize;
  invalid?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  /** Trailing unit or hint — `USD`, `sq ft`, `%`. Never focusable. */
  suffix?: ReactNode;
  /** Reserve and reveal a clear affordance. The slot is always reserved, so the
   *  caret never jumps when the first character is typed. */
  clearable?: boolean;
  onClear?: () => void;
  /** Render the value in IBM Plex Mono — refs, IDs, keys. */
  mono?: boolean;
  containerClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    size = 'md',
    invalid = false,
    iconLeft,
    iconRight,
    suffix,
    clearable = false,
    onClear,
    mono = false,
    className,
    containerClassName,
    disabled = false,
    value,
    defaultValue,
    onChange,
    onKeyDown,
    ...rest
  },
  ref,
) {
  const innerRef = useRef<HTMLInputElement>(null);
  const [uncontrolled, setUncontrolled] = useState(() => String(defaultValue ?? ''));
  const current = value !== undefined ? String(value ?? '') : uncontrolled;
  const filled = current.length > 0;

  const clear = useCallback(() => {
    const node = innerRef.current;
    if (node) {
      setNativeValue(node, '');
      node.focus();
    }
    if (value === undefined) setUncontrolled('');
    onClear?.();
  }, [onClear, value]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      onKeyDown?.(event);
      if (event.defaultPrevented) return;
      if (clearable && filled && event.key === 'Escape') {
        // Only swallow Escape when it actually did something, so a field inside
        // a dialog still closes the dialog once it is empty.
        event.preventDefault();
        event.stopPropagation();
        clear();
      }
    },
    [clear, clearable, filled, onKeyDown],
  );

  return (
    <div
      data-disabled={disabled ? '' : undefined}
      className={cn(
        'group',
        fieldShell({ size, invalid, disabled, focus: 'within' }),
        FIELD_PAD[size],
        containerClassName,
      )}
    >
      {/* Decoration, never content. The slot hides itself from the accessibility
          tree rather than trusting every caller to remember: a bare lucide
          `<svg>` has no `aria-hidden` of its own, so `iconLeft={<Mail />}` was
          reaching a screen reader as an unnamed `img` node beside the field it
          decorates. The clear affordance below already did this correctly. */}
      {iconLeft ? (
        <span
          aria-hidden="true"
          className="pointer-events-none flex shrink-0 items-center text-muted [&_svg]:size-3.5 [&_svg]:shrink-0 [&_svg]:-translate-y-[0.5px]"
        >
          {iconLeft}
        </span>
      ) : null}

      <input
        ref={composeRefs(ref, innerRef)}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        value={value}
        defaultValue={defaultValue}
        onChange={(event) => {
          if (value === undefined) setUncontrolled(event.target.value);
          onChange?.(event);
        }}
        onKeyDown={handleKeyDown}
        className={cn(
          'h-full min-w-0 flex-1 bg-transparent text-inherit',
          'placeholder:text-muted disabled:cursor-not-allowed',
          'focus-visible:outline-none!',
          // The UA number stepper is the one piece of chrome this frame does not
          // own: it paints inside the border on hover and crowds any suffix.
          '[appearance:textfield]',
          '[&::-webkit-outer-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none',
          '[&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none',
          mono && 'font-mono tracking-tight tnum',
          className,
        )}
        {...rest}
      />

      {clearable ? (
        <button
          type="button"
          aria-label="Clear"
          tabIndex={filled ? 0 : -1}
          aria-hidden={filled ? undefined : true}
          onClick={clear}
          className={cn(
            'grid size-4 shrink-0 place-items-center rounded-xs! text-muted',
            'transition-[opacity,background-color,color] duration-[var(--dur-fast)] ease-[var(--ease-out)]',
            'hover:bg-hover hover:text-primary',
            filled
              ? 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100'
              : 'pointer-events-none invisible',
          )}
        >
          <X className="size-3" aria-hidden="true" />
        </button>
      ) : null}

      {iconRight ? (
        <span
          aria-hidden="true"
          className="pointer-events-none flex shrink-0 items-center text-muted [&_svg]:size-3.5 [&_svg]:shrink-0 [&_svg]:-translate-y-[0.5px]"
        >
          {iconRight}
        </span>
      ) : null}

      {/* A unit belongs to its value: same size as the field's own text, and
          `--space-1` from the last digit rather than the frame's `--space-2`
          gutter, so `48` + `h` reads as one token instead of two. */}
      {suffix ? (
        <span className="pointer-events-none -ml-0.5 shrink-0 text-muted tnum">{suffix}</span>
      ) : null}
    </div>
  );
});
